/**
 * Reusable Annotation Overlay Component
 * Handles annotation rendering, selection, and resize handles
 */

import React from 'react';

interface AnnotationRect {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

interface AnnotationOverlayProps {
  annotations: AnnotationRect[];
  selectedAnnotationId: string | null;
  setSelectedAnnotationId: (id: string | null) => void;
  clearOtherSelection?: () => void;
  annotationStyle: 'fill' | 'outline';
  isAnnotationMode: boolean;
  currentAnnotation?: AnnotationRect | null;
  isDrawing?: boolean;
  onDeleteAnnotation?: (id: string) => void;
}

export const AnnotationOverlay: React.FC<AnnotationOverlayProps> = ({
  annotations,
  selectedAnnotationId,
  setSelectedAnnotationId,
  clearOtherSelection,
  annotationStyle,
  isAnnotationMode,
  currentAnnotation,
  isDrawing,
  onDeleteAnnotation
}) => {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {annotations.map((annotation) => {
        const isSelected = selectedAnnotationId === annotation.id;
        return (
          <div 
            key={annotation.id} 
            className={`annotation-body absolute transition-all duration-200 ${isSelected ? 'shadow-lg cursor-move' : 'cursor-pointer'} pointer-events-auto`}
            data-annotation-id={annotation.id}
            style={{
              left: `${annotation.x * 100}%`,
              top: `${annotation.y * 100}%`,
              width: `${annotation.width * 100}%`,
              height: `${annotation.height * 100}%`,
              border: `${isSelected ? '4px' : '3.6px'} solid ${isSelected ? '#3b82f6' : annotation.color}`,
              backgroundColor: annotationStyle === 'fill' 
                ? (isSelected ? `#3b82f630` : `${annotation.color}20`)
                : 'transparent',
              zIndex: isSelected ? 10 : 5,
            }}
            onMouseDown={(e) => {
              console.log(`🎯 Annotation clicked: ${annotation.id}, isSelected: ${isSelected}`);
              e.stopPropagation();
              if (isAnnotationMode) {
                if (!isSelected) {
                  setSelectedAnnotationId(annotation.id);
                  clearOtherSelection?.(); // Clear other panel's selection
                }
              }
            }}
          >
            {/* Resize handles for selected annotation */}
            {isSelected && (
              <>
                {/* Top-left handle */}
                <div
                  className="annotation-handle absolute w-3 h-3 bg-blue-500 border-2 border-white cursor-nw-resize pointer-events-auto"
                  data-annotation-id={annotation.id}
                  data-handle-type="nw"
                  style={{
                    left: '-6px',
                    top: '-6px',
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    console.log(`🎯 Annotation resize handle clicked: nw`);
                  }}
                />
                
                {/* Top-right handle */}
                <div
                  className="annotation-handle absolute w-3 h-3 bg-blue-500 border-2 border-white cursor-ne-resize pointer-events-auto"
                  data-annotation-id={annotation.id}
                  data-handle-type="ne"
                  style={{
                    right: '-6px',
                    top: '-6px',
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    console.log(`🎯 Annotation resize handle clicked: ne`);
                  }}
                />
                
                {/* Bottom-left handle */}
                <div
                  className="annotation-handle absolute w-3 h-3 bg-blue-500 border-2 border-white cursor-sw-resize pointer-events-auto"
                  data-annotation-id={annotation.id}
                  data-handle-type="sw"
                  style={{
                    left: '-6px',
                    bottom: '-6px',
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    console.log(`🎯 Annotation resize handle clicked: sw`);
                  }}
                />
                
                {/* Bottom-right handle */}
                <div
                  className="annotation-handle absolute w-3 h-3 bg-blue-500 border-2 border-white cursor-se-resize pointer-events-auto"
                  data-annotation-id={annotation.id}
                  data-handle-type="se"
                  style={{
                    right: '-6px',
                    bottom: '-6px',
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    console.log(`🎯 Annotation resize handle clicked: se`);
                  }}
                />

                {/* Delete button */}
                {onDeleteAnnotation && (
                  <div
                    className="annotation-delete absolute w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer flex items-center justify-center text-xs font-bold transition-colors duration-200 pointer-events-auto"
                    data-annotation-id={annotation.id}
                    style={{
                      right: '-10px',
                      top: '-10px',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log(`🗑️ Deleting annotation: ${annotation.id}`);
                      // Use requestAnimationFrame to ensure proper event handling in React 18
                      requestAnimationFrame(() => {
                        onDeleteAnnotation(annotation.id);
                      });
                    }}
                  >
                    ×
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      {/* Current drawing annotation */}
      {currentAnnotation && isDrawing && (
        <div className="absolute"
          style={{
            left: `${currentAnnotation.x * 100}%`,
            top: `${currentAnnotation.y * 100}%`,
            width: `${currentAnnotation.width * 100}%`,
            height: `${currentAnnotation.height * 100}%`,
            border: `3.6px solid ${currentAnnotation.color}`,
            backgroundColor: annotationStyle === 'fill' 
              ? `${currentAnnotation.color}20`
              : 'transparent',
          }} 
        />
      )}
    </div>
  );
};